//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { MentionWithIndices } from 'twitter-text';

import {
  ISocialTextEntity,
  SocialTextEntityType,
  SocialTextParseMethod,
} from '@utils/social-text-utils/socialTextUtilsModel';

// Match @ followed by alphanumeric characters, periods, or underscores
// Cannot start or end with a period or have two consecutive periods
// Testing in IG doesn't consistently need mentions to be separated by a space
// So we are defaulting to require a space
// IG usernames must be 30 characters or less, but IG displays mentions with more characters in captions/comments
// but user tagging is restricted to valid names (so 30 characters or less)
//
// We have two regexes because the SINGLE_INSTAGRAM_USERNAME_REGEX checks one username
// (to be used in validating a single user tag) and INSTAGRAM_MENTIONS_REGEX is used when we want
// to check usernames within a string (to be used in extracting from a caption)
export const SINGLE_INSTAGRAM_USERNAME_REGEX =
  /(^[0-9a-zA-Z_]+(\.[a-zA-Z0-9_]+)*)$/;
const INSTAGRAM_MENTIONS_REGEX =
  /(?:^|\s)+(@[a-zA-Z0-9_]+(?:\.[a-zA-Z0-9_]+)*)(?:@|\.|$|)/g;

const extractInstagramMentions = (text: string): MentionWithIndices[] => {
  return Array.from(text.matchAll(INSTAGRAM_MENTIONS_REGEX)).reduce(
    (mentions: MentionWithIndices[], match) => {
      const fullMatch = match[0];
      const mention = match[1];

      if (mention) {
        const startIndex = fullMatch.indexOf(mention) + match.index;
        mentions.push({
          screenName: mention,
          indices: [startIndex, startIndex + mention.length],
        });
      }
      return mentions;
    },
    [],
  );
};

/**
 * Takes text as input and parses it to return an array of typed InstagramMention and Text entities.
 * For example, the input '@bv! @a@b @c_..' would result in output:
 * [
 *   { type: 'InstagramMention', value: '@bv', start: 0, end: 3},
 *   { type: 'Text', value: '! ', start: 3, end: 5},
 *   { type: 'InstagramMention', value: '@a', start: 5, end: 7},
 *   { type: 'Text', value: '@b ', start: 7, end: 10},
 *   { type: 'InstagramMention', value: '@c_', start: 10, end: 13},
 *   { type: 'Text', value: '..', start: 13, end: 15},
 * ]
 *
 * @returns ISocialTextEntity<T>[] The parsed array of objects with properties
 * { type, value, start, end }, where type is one of SocialTextEntityType.
 */
export const parseInstagramTextAndMentions: SocialTextParseMethod = (text) => {
  const extractedMentions = extractInstagramMentions(text);

  if (extractedMentions.length === 0) {
    return [
      {
        type: SocialTextEntityType.Text,
        value: text,
        start: 0,
        end: text.length,
      },
    ];
  }

  const parsedTextValues: ISocialTextEntity[] = [];
  let currIndex = 0;
  extractedMentions.map((entity, index) => {
    const entityStartIndex = entity.indices[0];
    const entityEndIndex = entity.indices[1];

    if (entityStartIndex > currIndex) {
      const precedingText = text.slice(currIndex, entityStartIndex);
      parsedTextValues.push({
        type: SocialTextEntityType.Text,
        value: precedingText,
        start: currIndex,
        end: entityStartIndex,
      });
    }

    parsedTextValues.push({
      type: SocialTextEntityType.InstagramMention,
      value: entity.screenName,
      start: entityStartIndex,
      end: entityEndIndex,
    });

    if (index === extractedMentions.length - 1) {
      if (text.length > entityEndIndex) {
        const remainingText = text.slice(entityEndIndex);
        parsedTextValues.push({
          type: SocialTextEntityType.Text,
          value: remainingText,
          start: entityEndIndex,
          end: text.length,
        });
      }
    }

    currIndex = entityEndIndex;
  });

  return parsedTextValues;
};
